/* eslint-disable max-lines */
import {
  ITERATION_TYPE,
  PM_PROVIDERS,
  ARTICLE_IDS
} from 'common/constants/constants';
import Errors from 'common/constants/Errors';
import { SPACES } from 'common/constants/sizes';
import { getColor } from 'common/constants/_colors';
import TableSkeleton from 'components/base/Skeletons';
import {
  Flex,
  Separator,
  TableNoVerticalLines
} from 'components/base/layout-components';
import Table from 'components/base/Table';
import Text from 'components/base/Text';
import _ from 'lodash';
import {
  getIterationsTableColumns,
  getIterationsSkeletonColumns
} from '../../../../../services/project-management.service';
import Icon from 'components/base/Icon';
import { useEffect } from 'react';
import {
  JIRA_FILTER_TYPES_MENU_ITEMS,
  getIterationsTableData,
  formatFiltersFromDB
} from '../services/selection.service';
import { StyledButton, StyledIcon } from './selection.styles';
import { METHOD_NAMES } from 'modals/CreateEditProjectModal/consts';
import * as LighthouseService from 'common/services/lighthouse.service';
import { useSelector } from 'react-redux';
import { boardsIdKeysSelector } from 'state/selectors/boards';
import InfoBox from 'components/InfoBox';
import SearchInput from 'components/SearchInput';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import AzureBoardsTeamsAreasSelector from 'components/AzureBoardsTeamsAreasSelector';
import useSettingsService from 'components/hooks/useSettingsService';
import { pmFiltersBuilder } from 'components/FilterMenus/pm.filters';
import FilterMenusV2 from 'components/FilterMenus/FilterMenusV2';
import { FILTER_TYPES } from 'common/constants/filters';

// eslint-disable-next-line max-lines-per-function
const Selection = ({
  onTeamProjectSelection,
  projectsFormatedForDropdown,
  selectedProviderIds = [],
  itrationsType,
  onChangeIterationsType,
  iterations,
  regexTerm,
  setRegexTerm,
  errors,
  pmToolConfig,
  isLoadingIterations,
  selectedFilters,
  setSelectedFilters,
  isTeamPMConfigurationEnabled,
  cadence = METHOD_NAMES.SCRUM.type,
  setCadence,
  selectedFiltersOrigin,
  onChangeCadence,
  filteredIterations,
  onIterationSelection,
  hasIterations,
  isOnSaveAction
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();

  const menuItemsV2 = pmFiltersBuilder(
    {
      [PM_PROVIDERS.JIRA]: [
        FILTER_TYPES.BOARD,
        FILTER_TYPES.EPIC,
        FILTER_TYPES.LABEL,
        FILTER_TYPES.FIELD
      ],
      [PM_PROVIDERS.CLUBHOUSE]: [
        FILTER_TYPES.PROJECTS,
        FILTER_TYPES.TEAMS,
        FILTER_TYPES.EPIC,
        FILTER_TYPES.LABEL
      ]
    },
    getSettingOrDefinitionValue
  );

  const isGroup = useSelector(isSelectedTeamParentSelector);
  const dbBoardsMap = useSelector(boardsIdKeysSelector);

  useEffect(() => {
    JIRA_FILTER_TYPES_MENU_ITEMS.map(item => {
      if (_.get(item, 'title', {}) !== 'Jira Board') {
        item.disabled = !_.get(selectedFilters, FILTER_TYPES.BOARD, []).length;
      }
    });
  }, [selectedFilters]);

  useEffect(() => {
    if (pmToolConfig?.sensor_id === PM_PROVIDERS.AZURE_BOARDS) return;

    const selectedFiltersFromOrigin = formatFiltersFromDB(
      selectedFiltersOrigin
    );

    setSelectedFilters(selectedFiltersFromOrigin);
  }, []);

  return (
    <>
      <Flex items='center' pb='medium'>
        <Text size='medium' pr={SPACES.small} whiteSpace='nowrap'>
          Define scope to identify the team:
        </Text>
        <StyledIcon
          name='helpicon'
          size='smaller'
          color='nepal'
          onClick={() =>
            LighthouseService.navigateArticle(
              ARTICLE_IDS.SET_JIRA_BOARD_PER_TEAM // check if it needs to stay -> JIRA RELATED ONLY
            )
          }
        />

        {pmToolConfig?.sensor_id === PM_PROVIDERS.AZURE_BOARDS ? (
          <AzureBoardsTeamsAreasSelector
            defaultSelectedValues={
              selectedFilters[FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]
            }
            onChange={values =>
              setSelectedFilters(prev => ({
                ...prev,
                [FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS]: values
              }))
            }
          />
        ) : (
          <FilterMenusV2
            customFieldById
            menuItems={menuItemsV2}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
        )}
      </Flex>
      {!isGroup && (
        <Flex items='center' pb='small'>
          <Text size='medium' pr={SPACES.medium}>
            What is the team's methodology?
          </Text>
          <StyledButton
            items='center'
            ml='small'
            bgcolor={cadence === METHOD_NAMES.SCRUM.type}
            disabled={isOnSaveAction}
            onClick={() => {
              if (!isOnSaveAction) {
                setCadence(METHOD_NAMES.SCRUM.type);
                onChangeCadence(false);
              }
            }}
            data-testid='scrum-button'
          >
            <Icon name='scrum' color='black' />
            <Text pl='0.5rem' size='medium'>
              {' '}
              Scrum{' '}
            </Text>
          </StyledButton>
          <StyledButton
            items='center'
            ml='small'
            bgcolor={cadence === METHOD_NAMES.KANBAN.type}
            disabled={isOnSaveAction}
            onClick={() => {
              if (!isOnSaveAction) {
                setCadence(METHOD_NAMES.KANBAN.type);
                onChangeCadence(true);
              }
            }}
            data-testid='kanban-button'
          >
            <Icon name='kanban' color='black' />
            <Text pl='0.5rem' size='medium'>
              {' '}
              Kanban{' '}
            </Text>
          </StyledButton>
        </Flex>
      )}

      {((!isGroup &&
        itrationsType === ITERATION_TYPE.IMPORT &&
        !isTeamPMConfigurationEnabled) ||
        (isTeamPMConfigurationEnabled &&
          cadence === METHOD_NAMES.SCRUM.type)) && (
        <>
          <Separator
            width='100%'
            height='1px'
            color={getColor('catskillWhite')}
            margin='1rem 0 2rem 0'
          ></Separator>
          {iterations.length ? (
            <>
              <Text
                size='large'
                weight='500'
                textTransform='uppercase'
                pb={SPACES.small}
              >
                Iteration management
              </Text>
              <Text size='medium' color='shuttleGray' pb={SPACES.small}>
                Select the iterations you would like to see on LinearB
              </Text>
              <Flex pb='medium' items='center'>
                <Flex items='center'>
                  <Flex direction='column'>
                    {!hasIterations && (
                      <InfoBox
                        mt={null}
                        mb='small'
                        icon='alert'
                        iconAlign='center'
                        iconColor='orangePeel'
                        backgroundColor='orangePeel'
                        backgroundAlpha={0.1}
                        borderColor='orangePeel'
                        borderAlpha={0.2}
                      >
                        <Text size='x-medium' pl='0.5rem' weight='400'>
                          Please re-select your team iterations
                        </Text>
                      </InfoBox>
                    )}
                    <SearchInput
                      width='19rem'
                      height='3rem'
                      type='search'
                      onChange={setRegexTerm}
                      placeholder='Search by regular expression'
                      value={regexTerm}
                    />
                  </Flex>
                  {!_.isEmpty(errors) ? (
                    <Text pl={SPACES.small} size='small' color='torchRed'>
                      {Errors.SETTINGS.JIRA[errors]}
                    </Text>
                  ) : null}
                </Flex>
              </Flex>
            </>
          ) : null}
          {selectedProviderIds.length && isLoadingIterations ? (
            <TableSkeleton
              showSkeletonColumnsHead
              columns={getIterationsTableColumns}
              skeletonColumns={getIterationsSkeletonColumns}
            />
          ) : (
            <TableNoVerticalLines>
              <Table
                columns={getIterationsTableColumns}
                data={getIterationsTableData({
                  filteredIterations,

                  dbBoardsMap,
                  onIterationSelection
                })}
              />
            </TableNoVerticalLines>
          )}
        </>
      )}
    </>
  );
};

export default Selection;
